import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { getCycleDetail, CycleOrder } from '../api/singlePositionCycles';
import OrderDetailModal from './shared/OrderDetailModal';
import SinglePositionCycleContext from './SinglePositionCycleContext';
import './SinglePositionCycleOrders.css';

interface Props {
  tradeGroupId: string;
  cycleId: string;
}

const ROLE_LABELS: Record<string, string> = {
  ENTRY: 'Entry',
  // Second/third entry legs — SUPPORT_RETEST_DIP_LADDER's multi-leg entry (spec REQ-002/AD-9); every
  // other strategy only ever populates ENTRY.
  SECONDARY_ENTRY: 'Secondary Entry',
  TERTIARY_ENTRY: 'Tertiary Entry',
  PARTIAL_EXIT: 'Partial Exit',
  SECOND_PARTIAL_EXIT: 'Second Partial Exit',
  EXIT: 'Exit',
  PRIMARY_STOP: 'Primary Stop',
  SECONDARY_STOP: 'Secondary Stop',
  TERTIARY_STOP: 'Tertiary Stop',
  PENDING_STOP: 'Pending Stop',
  // Immutable maker sells paired one-per-entry-leg at SUPPORT_RETEST_DIP_LADDER's fixed target price.
  PRIMARY_TARGET_SELL: 'Primary Target Sell',
  SECONDARY_TARGET_SELL: 'Secondary Target Sell',
  TERTIARY_TARGET_SELL: 'Tertiary Target Sell',
};

function fmtNum(value: number | null | undefined, digits = 2): string {
  if (value === null || value === undefined) return '—';
  return value.toLocaleString(undefined, { minimumFractionDigits: digits, maximumFractionDigits: digits });
}

/**
 * Cycle drill-down (spec REQ-002/003): the orders of one cycle, by leg role. Selecting an order opens
 * the reusable OrderDetailModal with its full exchange execution. Rendered as an expanded row under a
 * cycle in SinglePositionCycleHistory.
 */
const SinglePositionCycleOrders: React.FC<Props> = ({ tradeGroupId, cycleId }) => {
  const [selected, setSelected] = useState<CycleOrder | null>(null);

  const { data, isLoading, isError, error } = useQuery({
    queryKey: ['single-position-cycle-detail', tradeGroupId, cycleId],
    queryFn: () => getCycleDetail(tradeGroupId, cycleId),
  });

  if (isLoading) return <div className="spc-orders-status">Loading orders…</div>;
  if (isError) return <div className="spc-orders-status spc-orders-error">{error instanceof Error ? error.message : 'Failed to load orders'}</div>;

  const orders = data?.orders ?? [];

  return (
    <div className="spc-orders">
      {data?.context && (
        <SinglePositionCycleContext context={data.context} tradeGroupType={data.tradeGroupType} />
      )}
      {orders.length === 0 ? (
        <div className="spc-orders-status">No orders recorded for this cycle.</div>
      ) : (
      <table className="data-table spc-orders-table">
        <thead>
          <tr>
            <th>Role</th>
            <th>Side</th>
            <th>Status</th>
            <th>Filled Size</th>
            <th>Limit Price</th>
            <th>Avg Fill Price</th>
            <th>Filled Value</th>
            <th>Total Fees</th>
            <th aria-label="actions" />
          </tr>
        </thead>
        <tbody>
          {orders.map((order) => (
            <tr key={order.clientOrderId} className="spc-orders-row" onClick={() => setSelected(order)}>
              <td>{ROLE_LABELS[order.role] ?? order.role}</td>
              <td>{order.side ? <span className={`badge badge-${order.side.toLowerCase()}`}>{order.side}</span> : '—'}</td>
              <td>{order.status ? <span className={`badge badge-status badge-${order.status.toLowerCase()}`}>{order.status}</span> : '—'}</td>
              <td className="price-cell">{fmtNum(order.filledSize, 8)}</td>
              <td className="price-cell">{fmtNum(order.limitPrice)}</td>
              <td className="price-cell">{fmtNum(order.averageFillPrice)}</td>
              <td className="price-cell">{fmtNum(order.filledValue)}</td>
              <td className="fee-cell">{fmtNum(order.totalFees)}</td>
              <td>
                <button type="button" className="spc-orders-detail-btn" onClick={(e) => { e.stopPropagation(); setSelected(order); }}>
                  Details
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      )}

      {selected && <OrderDetailModal order={selected} onClose={() => setSelected(null)} />}
    </div>
  );
};

export default SinglePositionCycleOrders;
